import type { ReactNode } from "react"
import React, { useCallback, useMemo, useState } from "react"

import {
  Battery100Icon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { useRecoilValue } from "recoil"

import type { LightReachPricing } from "~/api/dto/light-reach.dto"
import useFetchProposalPricing from "~/api/hooks/useFetchProposalPricing"
import { useFetchProposalPricingComparisons } from "~/api/hooks/useFetchProposalPricingComparisons"
import useFetchLightReachPricing from "~/api/hooks/useGetLightReachPricing"
import { useGetLightReachQuote } from "~/api/hooks/useGetLightReachQuotes"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import { currentUserMinimumMarginState } from "~/containers/Proposal/state/user/user-state"
import { FinanceType, MAX_PROPOSAL_MARGIN } from "~/utils/finance/constants"

import { StateLicenseForm } from "./StateLicenseForm"
import type { LightReachPricingWithMatch } from "./utils"
import {
  filterAvailableProducts,
  getGaugePositions,
  getSolarOnlyPpwBoundsFromComparisons,
  PRICE_TOLERANCE,
  PriceMatchType,
} from "./utils"
import { formatters } from "../Pricing/SystemPricing/utils"
interface LightReachFinancingProps {
  handleLightReachProductChange: (
    id: string | null,
    pricePerWatt?: number,
  ) => void
}

const TARGET_LABELS = {
  SOLAR_ONLY: `Solar-Only Target`,
  STANDARD: `Target`,
} as const

// Feature flag: View All pricing options only available in non-production
const VIEW_ALL_PRICING_ENABLED = true

export const LightReachFinancing: React.FC<LightReachFinancingProps> = ({
  handleLightReachProductChange,
}: LightReachFinancingProps): React.ReactNode => {
  const proposal = useProposalContext()

  const { data: proposalCost, isLoading: proposalCostLoading } =
    useFetchProposalPricing(proposal.local.id || ``)

  const {
    data,
    isLoading,
    isError,
    error,
    refetch: refetchPricing,
  } = useFetchLightReachPricing(proposal.local.id || ``)

  const currentUserMinimumMargin = useRecoilValue(currentUserMinimumMarginState)

  const requestedMargins = useMemo(() => {
    const margins = [currentUserMinimumMargin, MAX_PROPOSAL_MARGIN]
    return Array.from(new Set(margins)).sort((a, b) => a - b)
  }, [currentUserMinimumMargin])

  const { data: comparisonPricing } = useFetchProposalPricingComparisons(
    proposal.local.id || ``,
    requestedMargins,
  )

  const { data: lightReachQuote } = useGetLightReachQuote(
    proposal.local.id || ``,
  )

  // Don't show loading state when switching between products - prevents flash
  const isFinanceProductLoading = false

  // State for expanded "View All" mode
  const [expandedGroup, setExpandedGroup] = useState<{
    escalationRate: number
    productName: string
    allOptions: LightReachPricing[]
  } | null>(null)

  const totalCost =
    (proposal.remote.financeType !== FinanceType.LIGHT_REACH
      ? proposalCost?.cashPrice
      : proposalCost?.totalContractPrice) || 1
  const batteryCost = proposal.local.proposalCost.batteryCost || 0
  const systemSize = proposalCost?.systemSize || 1
  const currentMargin = proposal.local.margin ?? 0

  const targetText = useMemo(
    () => (batteryCost > 0 ? TARGET_LABELS.SOLAR_ONLY : TARGET_LABELS.STANDARD),
    [batteryCost],
  )

  const pricePerWatt =
    Math.round(((totalCost - batteryCost) / systemSize) * 100) / 100 //solar only

  const effectivePricePerWatt = Math.round((totalCost / systemSize) * 100) / 100 //total used for battery only

  const handleProductSelection = (
    pricing: LightReachPricing | LightReachPricingWithMatch,
  ) => {
    handleLightReachProductChange(pricing.productId, undefined)
  }

  const handleViewAll = (option: LightReachPricingWithMatch) => {
    setExpandedGroup({
      escalationRate: option.escalationRate,
      productName: option.name,
      allOptions: option.allOptions,
    })
  }

  const handleCloseExpandedView = () => {
    setExpandedGroup(null)
  }

  const selectedLightReachProductId: string | null =
    (proposal.local.selectedLoanProduct?.providerId ??
      proposal.local.financeProductId) &&
    lightReachQuote
      ? (proposal.local.selectedLoanProduct?.providerId ??
        proposal.local.financeProductId)
      : null

  // Memoize price range calculations
  const { minPpwRate, maxPpwRate } = useMemo((): {
    minPpwRate: number
    maxPpwRate: number
  } => {
    if (!data || Object.keys(data).length === 0) {
      return { minPpwRate: 0, maxPpwRate: 0 }
    }

    const allPpwRates = Object.values(data)
      .flat()
      .map((pricing: LightReachPricing): number => pricing.ppwRate)

    return {
      minPpwRate: Math.min(...allPpwRates),
      maxPpwRate: Math.max(...allPpwRates),
    }
  }, [data])

  const { requiredMinSolarOnlyPpw, requiredMaxSolarOnlyPpw } = useMemo(() => {
    const bounds = getSolarOnlyPpwBoundsFromComparisons(
      comparisonPricing,
      batteryCost,
    )

    return {
      requiredMinSolarOnlyPpw: bounds.min,
      requiredMaxSolarOnlyPpw: bounds.max,
    }
  }, [batteryCost, comparisonPricing])

  const catalogBounds = useMemo(
    () => ({ min: minPpwRate, max: maxPpwRate }),
    [maxPpwRate, minPpwRate],
  )

  const allowedBounds = useMemo(() => {
    const min = requiredMinSolarOnlyPpw ?? catalogBounds.min
    const max = requiredMaxSolarOnlyPpw ?? catalogBounds.max
    return { min, max }
  }, [
    catalogBounds.max,
    catalogBounds.min,
    requiredMaxSolarOnlyPpw,
    requiredMinSolarOnlyPpw,
  ])

  const filterWithinBounds = useCallback(
    (ppwRate: number): boolean => {
      return ppwRate >= allowedBounds.min && ppwRate <= allowedBounds.max
    },
    [allowedBounds.max, allowedBounds.min],
  )

  const selectedPricing = useMemo((): LightReachPricingWithMatch | null => {
    if (!data || !selectedLightReachProductId) {
      return null
    }

    const entry = Object.values(data).find((pricingArray: LightReachPricing[]) =>
      pricingArray.some(
        (pricing: LightReachPricing) =>
          pricing.productId === selectedLightReachProductId,
      ),
    )

    if (!entry) {
      return null
    }

    const allOptions = [...entry].sort((a, b) => a.ppwRate - b.ppwRate)
    const selected = entry.find(
      (pricing: LightReachPricing) =>
        pricing.productId === selectedLightReachProductId,
    )

    if (!selected) {
      return null
    }

    return {
      ...selected,
      matchType: PriceMatchType.SELECTED,
      allOptions,
    }
  }, [data, selectedLightReachProductId])

  // Memoize filtered products to prevent unnecessary recalculations
  const availableProducts = useMemo((): LightReachPricingWithMatch[] => {
    if (!data || Object.keys(data).length === 0) {
      return []
    }
    let filtered = filterAvailableProducts(
      data,
      pricePerWatt,
      selectedLightReachProductId,
      allowedBounds,
    )

    if (selectedPricing) {
      filtered = filtered.filter(
        (product) => product.escalationRate !== selectedPricing.escalationRate,
      )
      filtered.unshift(selectedPricing)
    }

    const deduped = filtered.filter((product, index, arr) => {
      return (
        arr.findIndex((p) => p.escalationRate === product.escalationRate) ===
        index
      )
    })

    return deduped.sort((a, b) => a.escalationRate - b.escalationRate)
  }, [
    allowedBounds,
    data,
    pricePerWatt,
    selectedLightReachProductId,
    selectedPricing,
  ])

  if (isLoading || proposalCostLoading || isFinanceProductLoading) {
    return <LightReachFinancingLoading />
  }

  const solarHeader = (
    targetText: string,
    pricePerWatt: number,
    currentMargin: number,
  ) => {
    return (
      <div className="flex justify-between align-middle">
        <p className="text-xl font-bold text-slate-500">Product and Pricing</p>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div>Current Margin:</div>
            <div className="text-xs text-gray-400">Your earnings per watt</div>
          </div>
          <div className="flex justify-end rounded-full bg-gray-200 p-4 py-2 text-sm font-semibold">
            {formatters.pricePerWatt(currentMargin)}/Watt
          </div>
          <div>
            <div>{targetText}</div>
            <div className="text-xs text-gray-400">System cost per watt</div>
          </div>
          <div className="flex justify-end rounded-full bg-gray-200 p-4 py-2 text-sm font-semibold">
            {formatters.pricePerWatt(pricePerWatt)}/Watt
          </div>
        </div>
      </div>
    )
  }

  const batteryHeader = (
    pricePerWatt: number,
    effectivePricePerWatt: number,
    currentMargin: number,
  ) => {
    return (
      <div className="flex justify-between align-middle">
        <p className="text-xl font-bold text-slate-500">Product and Pricing</p>
        <div className="flex gap-4">
          <div className="flex-row items-center justify-between gap-2">
            <div>Current Margin:</div>
            <div className="flex justify-end text-emerald-700 text-sm font-semibold">
              {formatters.pricePerWatt(currentMargin)}/Watt
            </div>
            <div className="text-xs text-gray-400">Your earnings per watt</div>
          </div>
          <div className="flex-row items-center justify-between gap-1">
            <div>Solar Target:</div>
            <div className="flex justify-end text-blue-700 text-sm font-semibold">
              {formatters.pricePerWatt(pricePerWatt)}/Watt
            </div>
            <div className="text-xs text-gray-400">System cost per watt</div>
          </div>
          <div className="flex-row items-center justify-between gap-1">
            <div>Effective Target:</div>
            <div className="flex justify-end text-blue-700 text-sm font-semibold">
              {formatters.pricePerWatt(effectivePricePerWatt)}/Watt
            </div>
            <div className="text-xs text-gray-400">Total cost per watt</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {!expandedGroup && batteryCost <= 0
        ? solarHeader(targetText, pricePerWatt, currentMargin)
        : batteryHeader(pricePerWatt, effectivePricePerWatt, currentMargin)}
      {isError &&
        ((): ReactNode => {
          const queryError = error as {
            message?: string
            errorType?: string
          } | null
          const errorMessage =
            queryError?.message || `Error loading LightReach products`
          const errorType = queryError?.errorType

          // Check for license error - show the license form
          const isLicenseError =
            errorType === `LIGHTREACH_LICENSE_ERROR` ||
            errorMessage.includes(`does not have a license for state`)

          if (isLicenseError) {
            const siteState = proposal.local.site?.state || ``
            const sellerId = proposal.local.sellerId || ``

            return (
              <StateLicenseForm
                state={siteState}
                userId={sellerId}
                onSuccess={() => {
                  refetchPricing()
                }}
              />
            )
          }

          // For other errors, show the error message
          return (
            <div className="rounded-md border border-red-300 bg-red-50 p-3">
              <p className="text-sm text-red-700">
                {((): ReactNode => {
                  if (errorType === `LIGHTREACH_AVAILABLE_ADDERS_ERROR`) {
                    if (errorMessage.includes(`backupBattery`)) {
                      return `Backup Battery is not available for this proposal on light reach. Please remove the Backup Battery  to continue.`
                    }
                    if (errorMessage.includes(`electricalUpgrade`)) {
                      return `Electrical Upgrade is not available for this proposal on light reach. Please remove the Electrical Upgrade adders to continue.`
                    }
                    return `${errorMessage}.`
                  }

                  // For other errors, show the message or a generic one
                  if (errorMessage.includes(`No LightReach account found`)) {
                    return `Unable to create LightReach account. Please contact support if this issue persists.`
                  }

                  // Check if error message already contains detailed info from external service
                  // (format: "Light Reach X failed: detailed message")
                  if (errorMessage.includes(`failed:`)) {
                    // Extract the detailed message after "failed:"
                    const detailedMessage = errorMessage
                      .split(`failed:`)[1]
                      ?.trim()
                    if (detailedMessage) {
                      return detailedMessage
                    }
                  }

                  // Vision validation messages (e.g. HO email) are already complete sentences.
                  if (/[.!?]$/.test(errorMessage)) {
                    return errorMessage
                  }

                  return `${errorMessage}. Please try again.`
                })()}
              </p>
            </div>
          )
        })()}
      {Object.keys(data ?? {}).length === 0 && !isLoading && !isError && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3">
          <p className="text-sm text-red-600">
            {`No Light Reach products available`}
          </p>
        </div>
      )}
      {Object.keys(data ?? {}).length > 0 &&
        !isLoading &&
        !proposalCostLoading &&
        !isError && (
          <div>
            <div className="mt-6 bg-white">
              {/* Header Row - Conditional based on expanded view */}
              {expandedGroup ? (
                <div className="flex items-start justify-between border-b border-gray-200 bg-gray-50 p-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {expandedGroup.productName} Options
                    </h3>
                    <p className="text-sm text-gray-600">
                      Select a product option for{` `}
                      {formatters.percentage(expandedGroup.escalationRate)}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="rounded bg-blue-100 px-2 py-1 text-sm font-medium text-blue-700">
                        ⊙ Closest Match
                      </span>
                      <span className="text-sm text-gray-500">
                        = Within ±${PRICE_TOLERANCE.toFixed(1)}/Watt of{` `}
                        {formatters.pricePerWatt(pricePerWatt)}/Watt target
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseExpandedView}
                    className="rounded p-1 hover:bg-gray-200"
                    aria-label="Close expanded view"
                  >
                    <XMarkIcon className="h-6 w-6 text-gray-500" />
                  </button>
                </div>
              ) : (
                <div
                  className="grid gap-3 border-b border-gray-200 bg-gray-50 p-4"
                  style={{
                    gridTemplateColumns:
                      batteryCost > 0
                        ? `2fr 1fr 1fr 1fr 1fr`
                        : `2fr 1fr 1fr 1.25fr`,
                  }}
                >
                  <div className="text-sm font-medium text-gray-700">
                    Product
                  </div>
                  <div className="text-center text-sm font-medium text-gray-700">
                    Solar Rate (per kWh)
                  </div>
                  <div className="text-center text-sm font-medium text-gray-700">
                    Est. Monthly Payment
                  </div>
                  <div className="text-center text-sm font-medium text-gray-700">
                    {batteryCost <= 0 ? `Actual $/Watt` : `Solar Only $/Watt`}
                  </div>
                  {batteryCost > 0 && (
                    <div className="text-center text-sm font-medium text-gray-700">
                      Effective $/Watt
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-white px-4 py-2 text-xs text-gray-600">
                <span className="font-medium text-gray-700">Margin impact:</span>
                <span
                  className="rounded bg-emerald-100 px-2 py-0.5 font-medium text-emerald-700"
                  title="This product's rate is above your target, increasing your profit margin"
                >
                  Margin Up
                </span>
                <span
                  className="rounded bg-red-100 px-2 py-0.5 font-medium text-red-700"
                  title="This product's rate is below your target, reducing your profit margin"
                >
                  Margin Down
                </span>
                <span
                  className="rounded bg-gray-100 px-2 py-0.5 font-medium text-gray-700"
                  title="This product's rate matches your target"
                >
                  On Target
                </span>
              </div>

              {Object.keys(data ?? {}).length === 0 && isLoading && (
                <div className="rounded-md border border-gray-300 bg-gray-50 p-3">
                  <p className="text-sm text-gray-600">
                    Loading LightReach products...
                  </p>
                </div>
              )}

              {/* Plan Rows - Conditional based on expanded view */}
              {expandedGroup ? (
                <>
                  {/* Column headers for expanded view - sticky */}
                  <div
                    className="sticky top-0 z-10 grid gap-3 border-b border-gray-200 bg-gray-50 p-4"
                    style={{
                      gridTemplateColumns:
                        batteryCost > 0
                          ? `2fr 1fr 1fr 1fr 1fr`
                          : `2fr 1fr 1fr 1.25fr`,
                    }}
                  >
                    <div className="text-sm font-semibold text-gray-700">
                      Product
                    </div>
                    <div className="text-center text-sm font-semibold text-gray-700">
                      Solar Rate (per kWh)
                    </div>
                    <div className="text-center text-sm font-semibold text-gray-700">
                      Est. Monthly Payment
                    </div>
                    <div className="text-center text-sm font-semibold text-gray-700">
                      {batteryCost <= 0 ? `Actual $/Watt` : `Solar Only $/Watt`}
                    </div>
                    {batteryCost > 0 && (
                      <div className="text-center text-sm font-semibold text-gray-700">
                        Effective $/Watt
                      </div>
                    )}
                  </div>
                  {/* Scrollable options list */}
                  <div className="max-h-80 overflow-y-auto">
                    {expandedGroup.allOptions
                      .filter((option: LightReachPricing) =>
                        filterWithinBounds(option.ppwRate),
                      )
                      .map((option: LightReachPricing) => {
                        const isClosestMatch =
                          Math.abs(option.ppwRate - pricePerWatt) <=
                          PRICE_TOLERANCE
                        return (
                          <LightReachFinanceOption
                            key={option.productId}
                            checked={
                              selectedLightReachProductId === option.productId
                            }
                            priceOption={option}
                            handleProductSelection={handleProductSelection}
                            isClosestMatch={isClosestMatch}
                            showViewAllButton={false}
                            systemSize={systemSize}
                            batteryCost={batteryCost}
                            targetPpw={pricePerWatt}
                          />
                        )
                      })}
                  </div>
                </>
              ) : availableProducts.length > 0 ? (
                availableProducts.map(
                  (exactPricing: LightReachPricingWithMatch) => (
                    <LightReachFinanceOption
                      key={exactPricing.productId}
                      checked={
                        selectedLightReachProductId === exactPricing.productId
                      }
                      priceOption={exactPricing}
                      handleProductSelection={handleProductSelection}
                      showViewAllButton={VIEW_ALL_PRICING_ENABLED}
                      onViewAll={handleViewAll}
                      systemSize={systemSize}
                      batteryCost={batteryCost}
                      targetPpw={pricePerWatt}
                    />
                  ),
                )
              ) : (
                (() => {
                  const isOutsideCatalog =
                    pricePerWatt > catalogBounds.max ||
                    pricePerWatt < catalogBounds.min
                  const isAbove = isOutsideCatalog
                    ? pricePerWatt > catalogBounds.max
                    : pricePerWatt > allowedBounds.max
                  const boundaryValue = isOutsideCatalog
                    ? isAbove
                      ? catalogBounds.max
                      : catalogBounds.min
                    : isAbove
                      ? allowedBounds.max
                      : allowedBounds.min
                  const gauge = getGaugePositions(
                    catalogBounds.min,
                    catalogBounds.max,
                    pricePerWatt,
                  )
                  const isSinglePoint = catalogBounds.min === catalogBounds.max

                  return (
                    <div className="space-y-4 p-4">
                      <div className="flex items-start gap-2">
                        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                        <div>
                          <p className="font-semibold text-gray-800">
                            LightReach PPA Not Available
                          </p>
                          <p className="mt-1 text-sm text-gray-600">
                            Your system&apos;s price per watt (
                            {formatters.pricePerWatt(pricePerWatt)}/W) is{` `}
                            {isAbove ? `above` : `below`}
                            {` `}
                            {isOutsideCatalog
                              ? `what LightReach PPA products support`
                              : `the margin-constrained range`}
                            {` `}({isAbove ? `up to` : `starting at`}
                            {` `}
                            {formatters.pricePerWatt(boundaryValue)}/W).
                          </p>
                        </div>
                      </div>

                      <div className="px-7">
                        <div className="relative h-6 w-full rounded bg-gray-200">
                          {isSinglePoint ? (
                            <div
                              className="absolute top-0 h-6 w-1 rounded bg-emerald-400"
                              style={{ left: `${gauge.barLeft}%` }}
                            />
                          ) : (
                            <div
                              className="absolute top-0 h-6 rounded bg-emerald-200"
                              style={{
                                left: `${gauge.barLeft}%`,
                                width: `${gauge.barWidth}%`,
                              }}
                            />
                          )}
                          <div
                            className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-amber-600 bg-amber-400"
                            style={{ left: `${gauge.targetPos}%` }}
                          />
                        </div>
                        <div className="relative mt-1 h-8 text-xs">
                          <span
                            className="absolute -translate-x-1/2 text-emerald-600"
                            style={{ left: `${gauge.barLeft}%` }}
                          >
                            {formatters.pricePerWatt(catalogBounds.min)}/W
                          </span>
                          {!isSinglePoint && (
                            <span
                              className="absolute -translate-x-1/2 text-emerald-600"
                              style={{
                                left: `${gauge.barLeft + gauge.barWidth}%`,
                              }}
                            >
                              {formatters.pricePerWatt(catalogBounds.max)}/W
                            </span>
                          )}
                          <span
                            className="absolute -translate-x-1/2 text-amber-500"
                            style={{ left: `${gauge.targetPos}%` }}
                          >
                            {formatters.pricePerWatt(pricePerWatt)}/W
                          </span>
                        </div>
                      </div>

                      <details className="px-7">
                        <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                          Why is this happening?
                        </summary>
                        <p className="mt-2 text-sm text-gray-500">
                          PPA pricing depends on how much your system costs per
                          watt of capacity. This is calculated by dividing the
                          total system cost by the system size. When this value
                          falls outside LightReach&apos;s product range, no PPA
                          options are available.
                        </p>
                      </details>

                      <div className="px-7">
                        <p className="text-sm font-medium text-gray-700">
                          To get into range:
                        </p>
                        <ul className="mt-1 list-disc pl-5 text-sm text-gray-500">
                          <li>
                            Adjust your system design to increase production or
                            lower costs
                          </li>
                          <li>
                            Reduce your target margin to bring the cost per watt
                            down
                          </li>
                        </ul>
                      </div>
                    </div>
                  )
                })()
              )}
            </div>
          </div>
        )}
      {batteryCost > 0 && (
        <div className="flex items-center justify-center space-x-6 rounded-md bg-blue-100 p-2 text-blue-500">
          <Battery100Icon className="flex h-4 w-4 -rotate-90" /> Estimated
          monthly payment includes your solar rate with battery upgrade
        </div>
      )}
    </div>
  )
}

type LightReachFinanceOptionProps = {
  priceOption: LightReachPricing | LightReachPricingWithMatch
  checked: boolean
  handleProductSelection: (
    option: LightReachPricing | LightReachPricingWithMatch,
  ) => void
  isClosestMatch?: boolean
  showViewAllButton?: boolean
  onViewAll?: (option: LightReachPricingWithMatch) => void
  systemSize: number
  batteryCost: number
  targetPpw: number
}
const LightReachFinanceOption: React.FC<LightReachFinanceOptionProps> = ({
  priceOption,
  handleProductSelection,
  checked,
  isClosestMatch = false,
  showViewAllButton = false,
  onViewAll,
  systemSize,
  batteryCost,
  targetPpw,
}: LightReachFinanceOptionProps): React.ReactNode => {
  const effectivePpw =
    priceOption.totalEPCPayment && systemSize > 0
      ? priceOption.totalEPCPayment / systemSize
      : null

  const marginDelta = priceOption.ppwRate - targetPpw
  const normalizedMarginDelta = Math.abs(marginDelta) < 0.001 ? 0 : marginDelta
  let marginImpact: `decrease` | `increase` | `no_change` = `no_change`

  if (normalizedMarginDelta > 0) {
    marginImpact = `increase`
  }

  if (normalizedMarginDelta < 0) {
    marginImpact = `decrease`
  }

  const marginImpactMeta: Record<
    typeof marginImpact,
    { label: string; className: string }
  > = {
    increase: {
      label: `Margin Up`,
      className: `rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700`,
    },
    decrease: {
      label: `Margin Down`,
      className: `rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700`,
    },
    no_change: {
      label: `On Target`,
      className: `rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700`,
    },
  }

  const formatPriceOptionName = (name: string, type: string): string => {
    if (type !== `ppa`) return name

    const PREFIX = `LightReach `

    if (!name.startsWith(PREFIX)) return name

    const withoutPrefix = name.substring(PREFIX.length)

    // Check if what remains is meaningful (not just punctuation/whitespace/percentage)
    const meaningfulContent = withoutPrefix.trim()

    // If it's just a dash and percentage, or empty, keep the original
    if (!meaningfulContent || meaningfulContent.match(/^[-\s]*\d+\.\d+%$/)) {
      return name
    }

    return withoutPrefix
  }

  const formatSolarRatePerKwh = (rate: number): string => {
    return new Intl.NumberFormat(`en-US`, {
      style: `currency`,
      currency: `USD`,
      minimumFractionDigits: 2,
      maximumFractionDigits: 3,
    }).format(rate)
  }

  return (
    <label
      key={priceOption.productId}
      className="group grid cursor-pointer gap-3 border-b border-gray-100 p-4 last:border-b-0 hover:bg-gray-50"
      style={{
        gridTemplateColumns:
          batteryCost > 0 ? `2fr 1fr 1fr 1fr 1fr` : `2fr 1fr 1fr 1.25fr`,
      }}
    >
      <div className="flex items-center gap-3">
        <input
          defaultValue={priceOption.productId}
          defaultChecked={checked}
          name="pricing-plan"
          type="radio"
          onChange={() => handleProductSelection(priceOption)}
          className="relative size-4 flex-shrink-0 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-blue-600 checked:bg-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
        />
        <div className="flex flex-col items-start gap-2">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">
              {formatPriceOptionName(priceOption.name, priceOption.type)}
            </span>
            {showViewAllButton && onViewAll && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onViewAll(priceOption as LightReachPricingWithMatch)
                }}
                className="rounded p-1 hover:bg-gray-200"
                aria-label="View all options for this product"
              >
                <EllipsisVerticalIcon className="h-5 w-5 text-gray-500" />
              </button>
            )}
          </div>
          {isClosestMatch && (
            <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
              Closest Match
            </span>
          )}
          <span className={marginImpactMeta[marginImpact].className}>
            {marginImpactMeta[marginImpact].label}
          </span>
        </div>
      </div>
      <div className="flex items-center justify-center">
        <span className="font-medium text-gray-900">
          {formatSolarRatePerKwh(priceOption.kwhRate)}/kWh
        </span>
      </div>
      <div className="flex items-center justify-center">
        <span className="font-medium text-gray-900">
          {formatters.currency(
            priceOption.monthlyPayments?.[0]?.totalMonthlyPayment || 0,
          )}
        </span>
      </div>
      <div className="flex items-center justify-center">
        <span className="font-medium text-gray-900">
          {formatters.pricePerWatt(priceOption.ppwRate)}/Watt
        </span>
      </div>
      {batteryCost > 0 && (
        <div className="flex items-center justify-center">
          <span className="font-medium text-gray-900">
            {effectivePpw !== null
              ? `${formatters.pricePerWatt(effectivePpw)}/Watt`
              : `-`}
          </span>
        </div>
      )}
    </label>
  )
}

const LightReachFinancingLoading = (): React.ReactNode => {
  return (
    <div className="space-y-4">
      <div role="status" className="mt-4 max-w-full animate-pulse space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="mb-2.5 h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
            <div className="h-2 w-32 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          </div>
          <div className="h-2.5 w-12 rounded-full bg-gray-300 dark:bg-gray-700"></div>
        </div>

        <span className="sr-only">Loading...</span>
      </div>
    </div>
  )
}
